// components/modals/HistoryErrorModal.js
import React, { useState } from "react";
import { exportToExcel, createErrorSummary } from "../../utils/jobUtils";

const HistoryErrorModal = ({ show, title, errorData, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, fixed, current

  if (!show) return null;

  // Categorize errors
  const fixedErrors = errorData.filter(
    (record) => record._wasFixed || record._isFixed
  );
  const currentErrors = errorData.filter(
    (record) => !record._wasFixed && !record._isFixed
  );

  // Filter data based on filter type and search
  const getFilteredData = () => {
    let filtered = errorData;

    // Apply type filter
    if (filterType === "fixed") {
      filtered = fixedErrors;
    } else if (filterType === "current") {
      filtered = currentErrors;
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((record) => {
        return (
          (record["เลขที่ใบกำกับ"] || "").toLowerCase().includes(searchLower) ||
          (record["Invoice No"] || "").toLowerCase().includes(searchLower) ||
          (record["รหัสลูกค้า"] || "").toLowerCase().includes(searchLower) ||
          (record["Outlet Code"] || "").toLowerCase().includes(searchLower) ||
          (record["Outlet Name"] || "").toLowerCase().includes(searchLower) ||
          (record["Remark"] || "").toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  };

  const filteredData = getFilteredData();
  const errorSummary = createErrorSummary(filteredData);

  const handleExport = () => {
    try {
      const exportData = filteredData.map((record, index) => ({
        ลำดับ: index + 1,
        สถานะ:
          record._wasFixed || record._isFixed
            ? "แก้ไขแล้ว"
            : "ข้อผิดพลาดปัจจุบัน",
        เลขที่ใบกำกับ: record["เลขที่ใบกำกับ"] || "",
        "Invoice No": record["Invoice No"] || "",
        วันที่: record["วันที่"] || "",
        "Calendar Day": record["Calendar Day"] || "",
        รหัสลูกค้า: record["รหัสลูกค้า"] || "",
        "Outlet Code": record["Outlet Code"] || "",
        "Outlet Name": record["Outlet Name"] || "",
        เลขที่บัญชี: record["เลขที่บัญชี"] || "",
        "Sales Route": record["Sales Route"] || "",
        "ค่าสินค้า/บริการ": parseFloat(record["ค่าสินค้า/บริการ"]) || 0,
        NIV: parseFloat(record["NIV"]) || 0,
        "NIV Diff": parseFloat(record["NIV Diff"]) || 0,
        ภาษีมูลค่าเพิ่ม: parseFloat(record["ภาษีมูลค่าเพิ่ม"]) || 0,
        VAT: parseFloat(record["VAT"]) || 0,
        "VAT Diff": parseFloat(record["VAT Diff"]) || 0,
        ข้อผิดพลาด: record["Remark"] || "",
        หมายเหตุการแก้ไข: record._fixedNote || "",
        วันที่แก้ไข: record._fixedDate || "",
      }));

      const filename = `history_errors_${filteredData.length}_records_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      exportToExcel(exportData, filename, "History Error Records");
    } catch (error) {
      console.error("Export failed:", error);
      alert("ไม่สามารถส่งออกได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "1200px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            🔍 รายละเอียดข้อผิดพลาด - {title}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {errorData.length > 0 ? (
            <>
              {/* Summary Alert */}
              <SummaryAlert
                total={errorData.length}
                fixed={fixedErrors.length}
                current={currentErrors.length}
                filtered={filteredData.length}
                title={title}
              />

              {/* Controls */}
              <ControlsSection
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filterType={filterType}
                onFilterChange={setFilterType}
                fixedCount={fixedErrors.length}
                currentCount={currentErrors.length}
                filteredCount={filteredData.length}
                totalCount={errorData.length}
                onExport={handleExport}
              />

              {/* Error Summary */}
              {errorSummary.length > 0 && (
                <ErrorTypeSummary summary={errorSummary} />
              )}

              {/* Error Records Table */}
              <ErrorRecordsTable data={filteredData} />

              {/* Bottom Summary */}
              <BottomSummary
                errorData={errorData}
                filteredData={filteredData}
                fixedErrors={fixedErrors}
                onExport={handleExport}
              />
            </>
          ) : (
            <EmptyState />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const SummaryAlert = ({ total, fixed, current, filtered, title }) => (
  <div style={{ marginBottom: "20px" }}>
    <div
      style={{
        background: "#fef3c7",
        border: "1px solid #f59e0b",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>📊</span>
      <div>
        <strong>ข้อผิดพลาดในช่วงเวลานี้: {total} รายการ</strong>
        {fixed > 0 && (
          <>
            <span style={{ color: "#059669", marginLeft: "8px" }}>
              (✅ แก้ไขแล้ว: {fixed})
            </span>
            {current > 0 && (
              <span style={{ color: "#dc2626", marginLeft: "8px" }}>
                (❌ ยังคงมี: {current})
              </span>
            )}
          </>
        )}
        <br />
        <small style={{ color: "#92400e" }}>
          {fixed > 0
            ? "✅ แสดงข้อมูลประวัติข้อผิดพลาดที่เก็บไว้ในฐานข้อมูล"
            : title.includes("สร้างงาน")
            ? "📊 แสดงข้อมูลประวัติข้อผิดพลาดจากการสร้างงาน"
            : "แสดงรายการที่มีข้อผิดพลาดในการเปรียบเทียบ ณ เวลาดังกล่าว"}
        </small>
      </div>
    </div>
  </div>
);

const ControlsSection = ({
  searchTerm,
  onSearchChange,
  filterType,
  onFilterChange,
  fixedCount,
  currentCount,
  filteredCount,
  totalCount,
  onExport,
}) => (
  <div
    style={{
      marginBottom: "20px",
      padding: "16px",
      background: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    }}
  >
    <div
      style={{
        display: "flex",
        gap: "16px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* Search */}
      <div style={{ flex: "1", minWidth: "250px" }}>
        <input
          type="text"
          placeholder="🔍 ค้นหาเลขที่ใบกำกับ, Invoice No, ข้อผิดพลาด..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Filter buttons */}
      <div style={{ display: "flex", gap: "8px" }}>
        <FilterButton
          active={filterType === "all"}
          onClick={() => onFilterChange("all")}
          count={totalCount}
        >
          ทั้งหมด
        </FilterButton>
        {fixedCount > 0 && (
          <FilterButton
            active={filterType === "fixed"}
            onClick={() => onFilterChange("fixed")}
            count={fixedCount}
            color="success"
          >
            แก้ไขแล้ว
          </FilterButton>
        )}
        {currentCount > 0 && (
          <FilterButton
            active={filterType === "current"}
            onClick={() => onFilterChange("current")}
            count={currentCount}
            color="error"
          >
            ยังคงมี
          </FilterButton>
        )}
      </div>

      {/* Export button */}
      <button className="btn btn-success btn-sm" onClick={onExport}>
        📥 ส่งออก ({filteredCount})
      </button>
    </div>
  </div>
);

const FilterButton = ({
  active,
  onClick,
  children,
  count,
  color = "default",
}) => {
  const getButtonStyle = () => {
    const baseStyle = {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "14px",
      border: "1px solid",
      cursor: "pointer",
      transition: "all 0.2s ease",
    };

    if (active) {
      const activeStyles = {
        success: {
          background: "#059669",
          color: "white",
          borderColor: "#059669",
        },
        error: {
          background: "#dc2626",
          color: "white",
          borderColor: "#dc2626",
        },
        default: {
          background: "#6366f1",
          color: "white",
          borderColor: "#6366f1",
        },
      };
      return { ...baseStyle, ...activeStyles[color] };
    } else {
      return {
        ...baseStyle,
        background: "white",
        color: "#64748b",
        borderColor: "#d1d5db",
      };
    }
  };

  return (
    <button style={getButtonStyle()} onClick={onClick}>
      {children} ({count})
    </button>
  );
};

const ErrorTypeSummary = ({ summary }) => (
  <div
    style={{
      marginBottom: "20px",
      padding: "16px",
      background: "#fef7ed",
      border: "1px solid #fed7aa",
      borderRadius: "8px",
    }}
  >
    <strong style={{ fontSize: "14px", color: "#9a3412" }}>
      📈 สรุปประเภทข้อผิดพลาด:
    </strong>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: "8px",
        marginTop: "8px",
      }}
    >
      {summary.slice(0, 6).map((item, index) => (
        <div
          key={index}
          style={{
            fontSize: "12px",
            padding: "4px 8px",
            background: "rgba(251, 146, 60, 0.1)",
            borderRadius: "4px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "#9a3412" }}>{item.type}</span>
          <strong style={{ color: "#ea580c" }}>{item.count}</strong>
        </div>
      ))}
      {summary.length > 6 && (
        <div
          style={{ fontSize: "12px", color: "#9a3412", fontStyle: "italic" }}
        >
          ...และอีก {summary.length - 6} ประเภท
        </div>
      )}
    </div>
  </div>
);

const ErrorRecordsTable = ({ data }) => (
  <div
    className="table-container"
    style={{
      maxHeight: "60vh",
      overflowY: "auto",
      overflowX: "auto",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
    }}
  >
    <table className="table" style={{ marginBottom: "0", minWidth: "1400px" }}>
      <thead
        style={{
          position: "sticky",
          top: "0",
          backgroundColor: "#fef2f2",
          zIndex: "10",
        }}
      >
        <tr>
          <th style={{ minWidth: "80px" }}>สถานะ</th>
          <th style={{ minWidth: "120px" }}>เลขที่ใบกำกับ</th>
          <th style={{ minWidth: "120px" }}>Invoice No</th>
          <th style={{ minWidth: "100px" }}>รหัสลูกค้า</th>
          <th style={{ minWidth: "120px" }}>Outlet Code</th>
          <th style={{ minWidth: "150px" }}>Outlet Name</th>
          <th style={{ minWidth: "100px" }}>เลขที่บัญชี</th>
          <th style={{ minWidth: "100px" }}>Sales Route</th>
          <th style={{ minWidth: "120px", textAlign: "right" }}>
            ค่าสินค้า/บริการ
          </th>
          <th style={{ minWidth: "100px", textAlign: "right" }}>NIV</th>
          <th style={{ minWidth: "120px", textAlign: "right" }}>
            ภาษีมูลค่าเพิ่ม
          </th>
          <th style={{ minWidth: "100px", textAlign: "right" }}>VAT</th>
          <th style={{ minWidth: "250px" }}>🚨 ข้อผิดพลาด</th>
        </tr>
      </thead>
      <tbody>
        {data.length > 0 ? (
          data.map((record, index) => (
            <HistoryErrorRow key={index} record={record} />
          ))
        ) : (
          <tr>
            <td colSpan="13" style={{ textAlign: "center", padding: "40px" }}>
              ไม่พบข้อมูลที่ตรงกับเงื่อนไข
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const HistoryErrorRow = ({ record }) => {
  const isFixed = record._wasFixed || record._isFixed;
  const rowColor = isFixed ? "#f0fdf4" : "#fef2f2";
  const textColor = isFixed ? "#059669" : "#dc2626";

  return (
    <tr style={{ backgroundColor: rowColor }}>
      <td style={{ textAlign: "center" }}>
        {isFixed ? (
          <span
            style={{
              fontSize: "12px",
              padding: "2px 6px",
              borderRadius: "4px",
              background: "#bbf7d0",
              color: "#059669",
            }}
          >
            ✅ แก้ไขแล้ว
          </span>
        ) : (
          <span
            style={{
              fontSize: "12px",
              padding: "2px 6px",
              borderRadius: "4px",
              background: "#fecaca",
              color: "#dc2626",
            }}
          >
            ❌ ข้อผิดพลาด
          </span>
        )}
      </td>
      <td
        style={{
          fontWeight: "600",
          fontFamily: "monospace",
          color: textColor,
        }}
      >
        {isFixed && "✅ "}
        {record["เลขที่ใบกำกับ"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          color: record["Invoice No"] ? "#059669" : textColor,
        }}
      >
        {record["Invoice No"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {record["รหัสลูกค้า"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {record["Outlet Code"] || "-"}
      </td>
      <td
        style={{
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {record["Outlet Name"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {record["เลขที่บัญชี"] || "-"}
      </td>
      <td
        style={{
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {record["Sales Route"] || "-"}
      </td>
      <td
        style={{
          fontWeight: "600",
          textAlign: "right",
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {(parseFloat(record["ค่าสินค้า/บริการ"]) || 0).toLocaleString()}
      </td>
      <td
        style={{
          fontWeight: "600",
          textAlign: "right",
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {(parseFloat(record["NIV"]) || 0).toLocaleString()}
      </td>
      <td
        style={{
          fontWeight: "600",
          textAlign: "right",
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {(parseFloat(record["ภาษีมูลค่าเพิ่ม"]) || 0).toLocaleString()}
      </td>
      <td
        style={{
          fontWeight: "600",
          textAlign: "right",
          fontFamily: "monospace",
          fontSize: "13px",
          color: isFixed ? "#059669" : "inherit",
        }}
      >
        {(parseFloat(record["VAT"]) || 0).toLocaleString()}
      </td>
      <td
        style={{
          fontSize: "12px",
          color: textColor,
          fontWeight: "600",
          backgroundColor: rowColor,
          padding: "8px",
          borderLeft: `3px solid ${textColor}`,
        }}
      >
        {isFixed && "✅ แก้ไขแล้ว: "}
        {record["Remark"] || "-"}
        {record._fixedNote && (
          <div
            style={{
              fontSize: "11px",
              color: "#059669",
              marginTop: "4px",
              fontStyle: "italic",
            }}
          >
            {record._fixedNote}
          </div>
        )}
      </td>
    </tr>
  );
};

const BottomSummary = ({ errorData, filteredData, fixedErrors, onExport }) => (
  <div
    style={{
      marginTop: "20px",
      padding: "16px",
      background: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <strong>สรุปข้อผิดพลาด:</strong>
        <ul
          style={{
            margin: "8px 0 0 20px",
            fontSize: "14px",
            color: "#64748b",
          }}
        >
          <li>
            📊 รายการที่มีข้อผิดพลาด: <strong>{errorData.length}</strong> รายการ
          </li>
          {fixedErrors.length > 0 && (
            <li style={{ color: "#059669" }}>
              ✅ ข้อผิดพลาดที่ถูกแก้ไข: <strong>{fixedErrors.length}</strong>{" "}
              รายการ
            </li>
          )}
          {filteredData.length !== errorData.length && (
            <li>
              🔍 รายการที่แสดง: <strong>{filteredData.length}</strong> รายการ
            </li>
          )}
          <li>
            📅 ข้อมูลนี้
            {fixedErrors.length > 0
              ? "ถูกเก็บไว้ในฐานข้อมูลแล้ว"
              : "แสดงสถานะข้อผิดพลาดในช่วงเวลาที่เลือก"}
          </li>
        </ul>
      </div>
      <button className="btn btn-success" onClick={onExport}>
        📥 ส่งออกประวัติข้อผิดพลาด Excel
      </button>
    </div>
  </div>
);

const EmptyState = () => (
  <div style={{ textAlign: "center", padding: "40px" }}>
    <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
    <h3>ไม่มีข้อผิดพลาด</h3>
    <p>ในช่วงเวลานี้ไม่มีข้อผิดพลาดในการเปรียบเทียบ</p>
    <div
      style={{
        marginTop: "20px",
        padding: "16px",
        background: "#f0fdf4",
        borderRadius: "8px",
        border: "1px solid #bbf7d0",
      }}
    >
      <strong style={{ color: "#059669" }}>🎉 สถานะ: ผ่านการตรวจสอบ</strong>
      <br />
      <small style={{ color: "#065f46" }}>
        ข้อมูลทั้งหมดในช่วงเวลานี้ตรงกันทุกประการ
      </small>
    </div>
  </div>
);

export default HistoryErrorModal;
